// src/components/layout/Header.js

import React, { useContext, useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import { ThemeContext } from '../../context/ThemeContext';
import logos from '../../assets/images/logo.png';

// Conteneur principal du header
const HeaderContainer = styled.header`
  position: fixed;
  width: 100%;
  z-index: 1000;
  background: ${props => props.isScrolled 
    ? props.theme.backgroundSecondary
    : props.theme.background};
  box-shadow: ${props => props.isScrolled 
    ? '0 4px 20px rgba(0, 0, 0, 0.1)'
    : 'none'};
  padding: ${props => props.isScrolled ? '0.5rem 2rem' : '1rem 2rem'};
  display: flex;
  justify-content: space-between;
  align-items: center;
  transition: all 0.3s ease;
`;

// Logo
const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  
  a {
    text-decoration: none;
    color: ${props => props.theme.primary};
    display: flex;
    align-items: center;
    
    img {
      height: 2.5rem;
      margin-right: 0.5rem;
    }
  }
`;

// Navigation principale
const Nav = styled.nav`
  display: flex;
  align-items: center;
  
  @media (max-width: 1024px) {
    display: none;
  }
`;

// Lien de navigation standard
const NavLink = styled(Link)`
  margin: 0 0.8rem;
  color: ${props => props.isActive 
    ? props.theme.primary 
    : props.theme.text};
  text-decoration: none;
  font-weight: 500;
  padding: 0.5rem 0.5rem;
  border-radius: 4px;
  transition: all 0.3s ease;
  
  &:hover {
    color: ${props => props.theme.primary};
    background: ${props => props.theme.backgroundHover};
  }
  
  &:active {
    transform: scale(0.98);
  }
`;

// Barre d'outils (langue et thème)
const ToolsContainer = styled.div`
  display: flex;
  align-items: center;
  margin-left: 1rem;
`;

// Bouton de changement de thème
const ThemeToggle = styled.button`
  background: ${props => props.theme.buttonBg};
  border: none;
  color: ${props => props.theme.buttonText};
  cursor: pointer;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  margin-left: 0.8rem;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.primary};
    color: white;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

// Bouton de changement de langue
const LanguageToggle = styled.button`
  background: ${props => props.theme.buttonBg};
  border: none;
  color: ${props => props.theme.buttonText};
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0.5rem 1rem;
  border-radius: 20px;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${props => props.theme.primary};
    color: white;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

// Bouton de menu pour mobile
const MenuButton = styled.button`
  display: none;
  background: ${props => props.theme.buttonBg};
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  color: ${props => props.theme.buttonText};
  font-size: 1.2rem;
  cursor: pointer;
  
  &:hover {
    background: ${props => props.theme.primary};
    color: white;
  }
  
  @media (max-width: 1024px) {
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

// Menu mobile - Fond plus foncé
const MobileNav = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100vh;
  background: ${props => props.theme.isDark 
    ? 'rgba(18, 18, 18, 0.98)' // Fond très foncé pour le mode sombre
    : 'rgba(245, 245, 245, 0.98)'}; // Fond très clair pour le mode clair
  z-index: 1001;
  padding: 2rem;
  display: ${props => props.isOpen ? 'flex' : 'none'};
  flex-direction: column;
  overflow-y: auto;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.15);
`;

// En-tête du menu mobile
const MobileHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid ${props => props.theme.border};
`;

// Lien mobile avec trait doré pour le menu actif
const MobileLink = styled(Link)`
  display: block;
  padding: 1rem;
  margin: 0.5rem 0;
  color: ${props => props.isActive ? props.theme.primary : props.theme.text};
  text-decoration: none;
  font-weight: 500;
  border-radius: 8px;
  transition: all 0.3s ease;
  position: relative;
  
  /* Ajout d'un trait doré sur le côté gauche pour le menu actif */
  ${props => props.isActive && `
    border-left: 3px solid gold;
    padding-left: 1.2rem;
    background: ${props.theme.isDark 
      ? 'rgba(255, 255, 255, 0.05)' 
      : 'rgba(0, 0, 0, 0.05)'};
  `}
  
  &:hover {
    background: ${props => props.theme.backgroundHover};
    color: ${props => props.theme.primary};
  }
`;

const Header = () => {
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const { theme, isDarkMode, toggleTheme } = useContext(ThemeContext);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Gestion du scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 30);
    };
    
    window.addEventListener('scroll', handleScroll);
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Fermeture du menu mobile lors d'un changement de route
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Changement de langue
  const changeLanguage = () => {
    i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr');
  };

  // Navigation items
  const navItems = [
    { name: t('common.home', 'Accueil'), path: '/' },
    { name: t('common.about', 'À propos'), path: '/about' },
    { name: t('common.services', 'Services'), path: '/services' },
    { name: t('common.articles', 'Articles'), path: '/blog' },
    { name: t('common.projects', 'Projets'), path: '/projects' },
    { name: t('common.formations', 'Formations'), path: '/formations' },
    { name: t('common.events', 'Événements'), path: '/events' },
    { name: t('common.solutions', 'Solutions'), path: '/solutions' },
    { name: t('common.gallery', 'Galerie'), path: '/gallery' },
    { name: t('common.community', 'community'), path: '/community' },
    { name: t('common.team', 'Our Team'), path: '/team' },
    { name: t('common.contact', 'Contact'), path: '/contact' } 
  ];

  // Vérifier si un chemin est actif
  const isActive = (path) => {
    if (path === '/') {
      return location.pathname === '/';
    }
    return location.pathname.startsWith(path);
  };

  return (
    <>
      <HeaderContainer
        theme={theme}
        isScrolled={isScrolled}
      >
        {/* Logo */}
        <Logo theme={theme}>
          <Link to="/">
            <img src={logos} alt="PropentaTech Logo" />
          </Link>
        </Logo>

        {/* Navigation desktop */}
        <Nav>
          {navItems.map((item, index) => (
            <NavLink
              key={index}
              to={item.path}
              theme={theme}
              isActive={isActive(item.path)}
            >
              {item.name}
            </NavLink>
          ))}
        </Nav>

        {/* Outils (langue et thème) */}
        <ToolsContainer>
          <LanguageToggle
            theme={theme}
            onClick={changeLanguage}
          >
            {i18n.language === 'fr' ? 'EN' : 'FR'}
          </LanguageToggle>
          
          <ThemeToggle
            theme={theme}
            onClick={toggleTheme}
            aria-label={isDarkMode ? "Passer au mode clair" : "Passer au mode sombre"}
          >
            {isDarkMode ? '☀️' : '🌙'}
          </ThemeToggle>
          
          {/* Bouton menu mobile */}
          <MenuButton 
            theme={theme} 
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          >
            {isMobileMenuOpen ? '✕' : '☰'}
          </MenuButton>
        </ToolsContainer>
      </HeaderContainer>

      {/* Menu mobile avec fond moins transparent et trait doré pour l'item actif */}
      <MobileNav 
        theme={{...theme, isDark: isDarkMode}} 
        isOpen={isMobileMenuOpen}
      >
        <MobileHeader theme={theme}>
          <Logo theme={theme}>
            <Link to="/">
              <img src={logos} alt="PropentaTech" />
              PropentaTech
            </Link>
          </Logo>
          <ThemeToggle
            theme={theme}
            onClick={toggleTheme}
          >
            {isDarkMode ? '☀️' : '🌙'}
          </ThemeToggle>
        </MobileHeader>
        
        {navItems.map((item, index) => (
          <MobileLink
            key={index}
            to={item.path}
            theme={{...theme, isDark: isDarkMode}}
            isActive={isActive(item.path)}
            onClick={() => setIsMobileMenuOpen(false)}
          >
            {item.name}
          </MobileLink>
        ))}
        
        <MobileLink 
          as="button"
          theme={{...theme, isDark: isDarkMode}}
          onClick={() => {
            changeLanguage();
            setIsMobileMenuOpen(false);
          }}
          style={{
            background: 'none',
            border: 'none',
            textAlign: 'left',
            width: '100%',
            cursor: 'pointer'
          }}
        >
          {i18n.language === 'fr' ? 'English' : 'Français'}
        </MobileLink>
      </MobileNav>
    </>
  );
};

export default Header;